/**
 * Compact relative-time formatter, used across row primitives, sheets, the
 * nudge list and cash-out history. "Just now" up to 1m, "Nm ago" up to 1h,
 * "Nh ago" up to 24h, then locale-formatted month + day.
 *
 * Returns `null`-safe shape via the wrapper: callers that may not have a
 * timestamp can use `formatRelativeOrNull` and short-circuit rendering.
 */
export function formatRelative(iso: string, now = Date.now()): string {
  const then = new Date(iso).getTime();
  // Server-controlled timestamps: a blank or malformed value renders nothing
  // rather than the literal "Invalid Date".
  if (Number.isNaN(then)) return '';
  const ms = now - then;
  if (ms < 60_000) return 'Just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Deal Steps captions ("SoFi confirmed · 3d ago (Sep 2)"): past a day, "3d
 * ago" alone loses the calendar and the date alone loses the wait, so both.
 * Scoped to those captions on purpose; every other screen keeps
 * `formatRelative`'s short date.
 */
export function formatRelativeDated(iso: string, now = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const hours = Math.floor((now - then) / 3_600_000);
  if (hours < 24) return formatRelative(iso, now);
  const days = Math.floor(hours / 24);
  return `${days}d ago (${formatRelative(iso, now)})`;
}

/**
 * A calendar day, never a distance: "today", else "Sep 9" (with the year
 * once it differs: "Sep 9, 2025"). The Refer tab's friend rows and sheet
 * use this everywhere: "1d ago" meant nothing to anyone (Jeff, 2026-09-13),
 * and a date is a fact the ambassador can act on. Malformed input renders
 * nothing rather than "Invalid Date".
 */
export function formatDay(iso: string, now = Date.now()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const today = new Date(now);
  if (
    then.getFullYear() === today.getFullYear()
    && then.getMonth() === today.getMonth()
    && then.getDate() === today.getDate()
  ) {
    return 'today';
  }
  const sameYear = then.getFullYear() === today.getFullYear();
  return then.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatRelativeOrNull(iso: string | null, now = Date.now()): string | null {
  if (!iso) return null;
  return formatRelative(iso, now);
}

/**
 * `formatRelativeDated` with non-breaking spaces, for the trailing " · time"
 * captions on deal steps and Refer rows that must not wrap between "just"
 * and "now". Mid-sentence, so lowercase here: "Marked done · Just now" read
 * as a typo. `formatRelative` keeps the capital where it stands alone.
 */
export function formatRelativeNoWrap(iso: string, now = Date.now()): string {
  return formatRelativeDated(iso, now)
    .replace(/^Just now/, 'just now')
    .replace(/ /g, '\u00A0');
}
